import React, { useState, useEffect } from 'react';
import { Smartphone, Tablet, Monitor, X, Eye, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../contexts/AuthContext';

const VIEWPORTS = {
  mobile: { width: 375, height: 667, label: 'Mobile', icon: Smartphone },
  tablet: { width: 768, height: 1024, label: 'Tablet', icon: Tablet },
  desktop: { width: 1280, height: 800, label: 'Desktop', icon: Monitor },
};

const ResponsivePreview = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [activeViewport, setActiveViewport] = useState('mobile');
  const [isMaximized, setIsMaximized] = useState(false);
  const [currentUrl, setCurrentUrl] = useState(window.location.href);

  // Only show for admin users
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  
  useEffect(() => {
    // Update URL when location changes
    const handleLocationChange = () => {
      setCurrentUrl(window.location.href);
    };
    window.addEventListener('popstate', handleLocationChange);
    return () => window.removeEventListener('popstate', handleLocationChange);
  }, []);

  if (!isAdmin) return null;

  const viewport = VIEWPORTS[activeViewport];
  const scale = isMaximized ? 1 : Math.min(
    (window.innerWidth * 0.8 - 80) / viewport.width,
    (window.innerHeight * 0.8 - 120) / viewport.height,
    1
  );

  return (
    <>
      {/* Floating Toggle Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-24 right-6 z-50 w-12 h-12 rounded-full bg-gradient-to-br from-violet-600 to-purple-700 text-white shadow-lg hover:shadow-xl hover:scale-110 transition-all flex items-center justify-center group"
          title="Responsive Preview"
          data-testid="responsive-preview-toggle"
        >
          <Eye className="w-5 h-5" />
          <span className="absolute right-14 bg-slate-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
            Responsive Preview
          </span>
        </button>
      )}

      {/* Preview Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className={`bg-slate-900 rounded-xl border border-slate-700 flex flex-col ${isMaximized ? 'w-full h-full' : 'max-w-[95vw] max-h-[95vh]'}`}>
            {/* Header */}
            <div className="flex items-center justify-between p-3 border-b border-slate-700">
              <div className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-violet-400" />
                <span className="font-medium text-white">Responsive Preview</span>
                <span className="text-xs text-slate-400 ml-2">
                  {viewport.width} × {viewport.height}
                </span>
              </div>
              
              {/* Viewport Switcher */}
              <div className="flex items-center gap-1 bg-slate-800 rounded-lg p-1">
                {Object.entries(VIEWPORTS).map(([key, vp]) => {
                  const Icon = vp.icon;
                  return (
                    <button
                      key={key}
                      onClick={() => setActiveViewport(key)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
                        activeViewport === key 
                          ? 'bg-violet-600 text-white' 
                          : 'text-slate-400 hover:text-white hover:bg-slate-700'
                      }`}
                      data-testid={`viewport-${key}`}
                    >
                      <Icon className="w-4 h-4" />
                      <span className="hidden sm:inline">{vp.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsMaximized(!isMaximized)}
                  className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
                  title={isMaximized ? 'Minimize' : 'Maximize'}
                >
                  {isMaximized ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
                  data-testid="close-preview"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* URL Bar */}
            <div className="px-3 py-2 border-b border-slate-800">
              <div className="flex items-center gap-2 bg-slate-800 rounded-lg px-3 py-2">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <input
                  type="text"
                  value={currentUrl}
                  onChange={(e) => setCurrentUrl(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && setCurrentUrl(e.target.value)}
                  className="flex-1 bg-transparent text-sm text-slate-300 outline-none"
                  placeholder="Enter URL to preview..."
                />
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => setCurrentUrl(window.location.href)}
                  className="text-xs text-violet-400 hover:text-violet-300"
                >
                  Current Page
                </Button>
              </div>
            </div>

            {/* Preview Frame */}
            <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-[#1a1a2e]">
              <div 
                className="bg-white rounded-lg shadow-2xl overflow-hidden transition-all duration-300"
                style={{
                  width: viewport.width,
                  height: viewport.height,
                  transform: `scale(${scale})`,
                  transformOrigin: 'center center',
                }}
              >
                {/* Device Frame */}
                <div className={`relative w-full h-full ${activeViewport === 'mobile' ? 'rounded-[20px] border-[8px] border-slate-800' : ''}`}>
                  {activeViewport === 'mobile' && (
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-slate-800 rounded-b-xl z-10" />
                  )}
                  <iframe
                    src={currentUrl}
                    title="Responsive Preview"
                    className="w-full h-full border-0"
                    style={{ 
                      width: viewport.width - (activeViewport === 'mobile' ? 16 : 0),
                      height: viewport.height - (activeViewport === 'mobile' ? 16 : 0),
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 py-2 border-t border-slate-800 flex items-center justify-between text-xs text-slate-500">
              <span>Scale: {Math.round(scale * 100)}%</span>
              <span>Tip: Use this to test layouts without switching devices</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ResponsivePreview;
